import React from 'react';
import { InfoIcon, iconSizes } from '@nebula/icons/ui';
import nebulaTheme from '@nebula/theme';
import {
  FeatureListCard,
  FeatureRowItem,
  FeatureRowButton,
  RowAnnouncement,
  FeatureTopRow,
  FeatureName,
  FeatureText,
  FeatureLastWord,
  FeatureValueCell,
  FeatureQualifierText,
  FeatureInfoIcon,
} from './index.styles';
import type { Feature, FeatureValueData, PlanId } from '../../../../types';
import EntitlementValue from '../../../../shared/EntitlementValue';
import { getEntitlementCellAnnouncement, splitTrailingWord } from '../../../../utils';

interface EntitlementsListProps {
  features: Feature[];
  featureKey: PlanId;
  planName: string;
  onSelectFeature: (feature: Feature) => void;
}

interface FeatureRowProps {
  feature: Feature;
  value?: FeatureValueData;
  planName: string;
  onSelectFeature: (feature: Feature) => void;
}

const FeatureRow: React.FC<FeatureRowProps> = ({ feature, value, planName, onSelectFeature }) => {
  const qualifier = value?.included ? value.text : undefined;
  const [labelStart, labelLastWord] = splitTrailingWord(feature.label);
  const announcement = getEntitlementCellAnnouncement(planName, feature.label, value);

  const visibleContent = (
    <>
      <FeatureTopRow aria-hidden="true">
        <FeatureName>
          <FeatureText $dimmed={value?.included === false}>
            {labelStart}
            <FeatureLastWord>
              {labelLastWord}
              {feature.info && (
                <FeatureInfoIcon>
                  <InfoIcon iconSize={iconSizes.xxSmall} iconColor={nebulaTheme.color.ui.blue[70]} aria-hidden="true" />
                </FeatureInfoIcon>
              )}
            </FeatureLastWord>
          </FeatureText>
        </FeatureName>
        <FeatureValueCell>
          <EntitlementValue value={value} iconSize={iconSizes.small} iconOnly />
        </FeatureValueCell>
      </FeatureTopRow>
      {qualifier && <FeatureQualifierText aria-hidden="true">{qualifier}</FeatureQualifierText>}
    </>
  );

  if (feature.info) {
    return (
      <FeatureRowButton onClick={() => onSelectFeature(feature)}>
        {/* The sentence has to be a real box the size of the row. An aria-label has no
            box, so the screen reader cursor drops onto the next row. */}
        <RowAnnouncement as="span">{announcement}</RowAnnouncement>
        {visibleContent}
      </FeatureRowButton>
    );
  }

  return (
    <FeatureRowItem>
      <RowAnnouncement>{announcement}</RowAnnouncement>
      {visibleContent}
    </FeatureRowItem>
  );
};

const EntitlementsList: React.FC<EntitlementsListProps> = ({ features, featureKey, planName, onSelectFeature }) => (
  <FeatureListCard>
    {features.map(feature => (
      <FeatureRow
        key={feature.label}
        feature={feature}
        value={feature.values[featureKey]}
        planName={planName}
        onSelectFeature={onSelectFeature}
      />
    ))}
  </FeatureListCard>
);

export default EntitlementsList;
